/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Store } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { SnackbarModuleStore } from '@opengeoweb/snackbar';
import { snackbarReducer, snackbarListener } from '@opengeoweb/snackbar';
import type {
  WebMapStateModuleState,
  SynchronizationGroupModuleState,
  UIModuleState,
} from '@opengeoweb/store';
import {
  mapReducer,
  serviceReducer,
  layerReducer,
  syncGroupsReducer,
  uiReducer,
  mapListener,
  serviceListener,
  layersListener,
  syncGroupsListener,
  genericListener,
} from '@opengeoweb/store';
import { dataExplorerListener } from './listeners';
import { dataExplorerReducer } from './reducer';
import type { DataExplorerModuleState } from './types';

export const dataExplorerReducersMap = {
  webmap: mapReducer,
  services: serviceReducer,
  layers: layerReducer,
  syncGroups: syncGroupsReducer,
  ui: uiReducer,
  dataExplorer: dataExplorerReducer,
  snackbar: snackbarReducer,
};

export const dataExplorerMiddlewares = [
  mapListener.middleware,
  serviceListener.middleware,
  layersListener.middleware,
  syncGroupsListener.middleware,
  genericListener.middleware,
  dataExplorerListener.middleware,
  snackbarListener.middleware,
];

const dataExplorerRootReducer = combineReducers(dataExplorerReducersMap);

export const createMockStore = (
  mockState?:
    | WebMapStateModuleState
    | SynchronizationGroupModuleState
    | UIModuleState
    | DataExplorerModuleState
    | SnackbarModuleStore,
): Store =>
  configureStore({
    reducer: dataExplorerRootReducer,
    preloadedState: mockState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(dataExplorerMiddlewares),
  });
